import { useEffect, useMemo, useState } from 'react';
import { Modal } from '../Modal';
import { contentService, imagesService } from '../../services/api';
import { formatApptDateLong } from '../../utils/dateFormatters';
import PhotoLibrary from '../PhotoLibrary';
import './PhotoPicker.css';

const KIND = { before: 'Before', after: 'After', customer: 'From the client' };
const FLAG_LABEL = { house_number: 'house number', number_plate: 'number plate', face: 'face', street_sign: 'street sign' };

function flagsOf(note) {
  if (!note?.privacy) return [];
  const f = Object.entries(note.privacy).filter(([k, v]) => k !== 'other' && v === true).map(([k]) => FLAG_LABEL[k] || k);
  if (note.privacy.other) f.push(note.privacy.other);
  return f;
}

/**
 * Pick job photos for a post (Content Studio, GID-Session-025). Only jobs whose
 * customer said yes to socials can be picked from; the tick records that yes.
 * "Check photos" asks the AI what each shows, flags anything private and marks
 * the best before/after pairs.
 * The second source is our own photo library (loaded from our computers and phones).
 *   onAdd(ids)               add the picked job photos
 *   onAddLibrary(ids)        add the picked library photos
 *   onBeforeAfter(b, a)      make the before/after image: job photo ids, or { library: id }
 */
export default function PhotoPicker({ isOpen, onClose, onAdd, onAddLibrary, onBeforeAfter, focusJobId = null, defaultSource = 'jobs', aiReady = true }) {
  const [source, setSource] = useState(defaultSource);
  const [jobs, setJobs] = useState(null);
  const [picked, setPicked] = useState([]);
  const [checking, setChecking] = useState({}); // jobId -> true
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setPicked([]); setError(''); setSource(defaultSource);
    contentService.jobs().then((r) => (r.success ? setJobs(r.jobs) : setError(r.error)));
  }, [isOpen]);

  const ordered = useMemo(() => {
    if (!jobs) return [];
    const list = focusJobId ? [...jobs].sort((a, b) => (b.id === focusJobId) - (a.id === focusJobId)) : jobs;
    return showAll ? list : list.filter((j) => j.photos.some((p) => p.kind !== 'customer') || j.id === focusJobId);
  }, [jobs, focusJobId, showAll]);

  const pickedRows = useMemo(() => (jobs || []).flatMap((j) => j.photos).filter((p) => picked.includes(p.id)), [jobs, picked]);
  const oneEach = pickedRows.length === 2 && pickedRows.some((p) => p.kind === 'before') && pickedRows.some((p) => p.kind === 'after');

  async function toggleSocials(job) {
    const r = await contentService.setSocials(job.id, !job.socials_ok);
    if (!r.success) return setError(r.error);
    setJobs((list) => list.map((j) => (j.id === job.id ? { ...j, socials_ok: r.job.socials_ok } : j)));
    if (!r.job.socials_ok) setPicked((ids) => ids.filter((id) => !job.photos.some((p) => p.id === id)));
  }

  async function check(job) {
    setChecking((c) => ({ ...c, [job.id]: true })); setError('');
    const r = await contentService.pollAi(contentService.photoCheck(job.id));
    setChecking((c) => ({ ...c, [job.id]: false }));
    if (!r.success) return setError(r.error);
    setJobs((list) => list.map((j) => (j.id === job.id ? { ...j, notes: r.result, notes_stale: false } : j)));
  }

  function toggle(photo, job) {
    if (!job.socials_ok) return;
    setPicked((ids) => (ids.includes(photo.id) ? ids.filter((id) => id !== photo.id) : [...ids, photo.id]));
  }

  async function add() {
    setBusy(true);
    await onAdd(picked);
    setBusy(false);
  }

  async function makeBa() {
    setBusy(true);
    const b = pickedRows.find((p) => p.kind === 'before');
    const a = pickedRows.find((p) => p.kind === 'after');
    await onBeforeAfter(b.id, a.id);
    setBusy(false);
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Photos" size="xl">
      <div className="pp-source" role="tablist">
        <button type="button" role="tab" aria-selected={source === 'jobs'} className={`content-tab${source === 'jobs' ? ' on' : ''}`} onClick={() => setSource('jobs')}>Job photos</button>
        {onAddLibrary && <button type="button" role="tab" aria-selected={source === 'library'} className={`content-tab${source === 'library' ? ' on' : ''}`} onClick={() => setSource('library')}>Our photo library</button>}
      </div>
      {source === 'library' ? (
        <PhotoLibrary pickMode aiReady={aiReady} onAdd={onAddLibrary} onBeforeAfter={onBeforeAfter} />
      ) : (
      <div className="photo-picker">
        <p className="text-muted pp-intro">
          Only jobs whose customer said yes to their photos on our Facebook can be used. Tick the box once you've asked them.
        </p>
        {error && <div className="form-error mb-md">{error}</div>}
        {!jobs ? <div className="empty-state"><div className="loading-spinner"></div></div> : ordered.length === 0 ? (
          <div className="empty-state"><p>No job has before or after photos yet. Add them on a visit, then come back.</p></div>
        ) : ordered.map((job) => {
          const byId = new Map((job.notes?.photos || []).map((n) => [n.id, n]));
          const bestPairs = (job.notes?.pairs || []).filter((p) => p.same_spot).sort((a, b) => b.wow - a.wow).slice(0, 3);
          const inBest = new Set(bestPairs.flatMap((p) => [p.before_id, p.after_id]));
          return (
            <section key={job.id} className={`pp-job${job.socials_ok ? '' : ' pp-job-locked'}`} data-job={job.id}>
              <header className="pp-job-head">
                <div>
                  <h3>{job.title || 'Job'} <span className="text-muted">{job.job_number}</span></h3>
                  <p className="text-muted">
                    {[job.suburb, job.services.join(', '), job.first_day && (job.first_day === job.last_day ? formatApptDateLong(`${job.first_day}T02:00:00Z`) : `${formatApptDateLong(`${job.first_day}T02:00:00Z`)} – ${formatApptDateLong(`${job.last_day}T02:00:00Z`)}`)].filter(Boolean).join(' · ')}
                  </p>
                </div>
                <div className="pp-job-actions">
                  <label className="pp-socials">
                    <input type="checkbox" checked={job.socials_ok} onChange={() => toggleSocials(job)} />
                    Customer said yes to socials
                  </label>
                  {job.socials_ok && (
                    <button type="button" className="btn btn-outline btn-sm" disabled={checking[job.id]} onClick={() => check(job)}>
                      {checking[job.id] ? 'Checking… (~15 s)' : job.notes ? 'Check again' : '✨ Check photos'}
                    </button>
                  )}
                </div>
              </header>
              {job.notes?.summary && <p className="pp-summary">✨ {job.notes.summary}</p>}
              {job.notes_stale && <p className="pp-summary text-muted">Photos have changed since the last check.</p>}
              {bestPairs.length > 0 && (
                <p className="pp-pairs">Best pairs: {bestPairs.map((p) => `#${p.before_id} → #${p.after_id} (${p.wow}/10)`).join(' · ')}</p>
              )}
              <div className="pp-thumbs">
                {job.photos.map((p) => {
                  const note = byId.get(p.id);
                  const flags = flagsOf(note);
                  const on = picked.includes(p.id);
                  return (
                    <button key={p.id} type="button" className={`pp-thumb${on ? ' on' : ''}${inBest.has(p.id) ? ' best' : ''}`}
                      disabled={!job.socials_ok} onClick={() => toggle(p, job)} title={note?.shows || p.original_name || KIND[p.kind]} data-photo={p.id}>
                      <img src={imagesService.fileUrl(p.id, 'thumb')} alt={note?.shows || KIND[p.kind]} loading="lazy" />
                      <span className={`pp-kind kind-${p.kind}`}>{KIND[p.kind]} #{p.id}</span>
                      {on && <span className="pp-tick">✓</span>}
                      {note && <span className="pp-quality" title="Photo quality">{'★'.repeat(Math.max(1, Math.min(5, note.quality)))}</span>}
                      {flags.length > 0 && <span className="pp-flag" title={`Shows a ${flags.join(', ')}`}>⚠ {flags.join(', ')}</span>}
                    </button>
                  );
                })}
              </div>
            </section>
          );
        })}
        {jobs && jobs.some((j) => !j.photos.some((p) => p.kind !== 'customer')) && (
          <button type="button" className="btn btn-link btn-sm" onClick={() => setShowAll((v) => !v)}>
            {showAll ? 'Hide jobs with only client photos' : 'Show jobs with only client photos too'}
          </button>
        )}
      </div>
      )}
      <div className="modal-actions pp-actions">
        {source === 'jobs' && <span className="text-muted pp-count">{picked.length ? `${picked.length} picked` : 'Tap photos to pick them'}</span>}
        <button type="button" className="btn btn-secondary" onClick={onClose}>{source === 'library' ? 'Close' : 'Cancel'}</button>
        {source === 'jobs' && onBeforeAfter && oneEach && (
          <button type="button" className="btn btn-outline" disabled={busy} onClick={makeBa}>Make a before/after image</button>
        )}
        {source === 'jobs' && (
          <button type="button" className="btn btn-primary" disabled={!picked.length || busy} onClick={add}>
            {busy ? 'Adding…' : `Add ${picked.length || ''} photo${picked.length === 1 ? '' : 's'}`}
          </button>
        )}
      </div>
    </Modal>
  );
}
